import EXTERNAL_SEARCH_CONFIG from '@/app/config/search'
import { ROUTES } from '@/app/lib/constants'
import { loadLocaleFiles } from '@/app/lib/load-locale'
import { generateTypesenseScopedKey } from '@/app/modules/search/lib/keys'
import { ExternalSearchCoursesPage } from '@/app/modules/search/pages/ExternalSearchCoursesPage'
import { SearchCoursesPage } from '@/app/modules/search/pages/SearchCoursesPage'
import { ApiService } from '@/app/services/ApiService'
import { CourseService } from '@/app/services/courses'
import { ICategory } from '@/types/category'
import { IEducationProvider } from '@/types/education-provider'
import { isBoom } from '@hapi/boom'

type ScopedApiKeys = {
  courses: string
}

const cookieName = process.env.NEXT_PUBLIC_AUTH_COOKIE_NAME
// This is the API key used to generate scoped search keys - it should NOT be exposed to the client
const frontendSearchApiKey = process.env.NEXT_SEARCH_INDEX_FRONTEND_API_KEY

export async function getServerSideProps(context) {
  const { req, query, locale } = context

  const messages = await loadLocaleFiles(locale, [
    'navigation',
    'strings',
    'search-page',
    'error',
  ])

  if (EXTERNAL_SEARCH_CONFIG.enabled) {
    if (!frontendSearchApiKey) {
      throw new Error('NEXT_SEARCH_INDEX_FRONTEND_API_KEY is not configured')
    }

    const scopedApiKeys: ScopedApiKeys = {
      // Filter to only show published courses
      courses: generateTypesenseScopedKey(
        frontendSearchApiKey,
        '{"filter_by":"published:=true"}'
      ),
    }

    return {
      props: {
        messages,
        scopedApiKeys,
      },
    }
  }

  const { page = 1, limit = 16, ...filters } = query

  try {
    ApiService.setAccessToken(req.cookies?.[cookieName!] || '')
    ApiService.setHeader('accept-language', locale ?? 'en')

    const initialCoursesResponse = await CourseService.getCourses(
      {
        ...filters,
      },
      {
        page,
        limit,
      },
      {
        qualityScore: 'desc',
        random: 'desc',
      }
    )

    let filterTitle = ''

    if (initialCoursesResponse.data.length > 0) {
      const { educationProvider, categories = [] } =
        initialCoursesResponse.data[0]
      if (
        Array.isArray(filters.educationProvider) &&
        filters.educationProvider.length === 1 &&
        educationProvider
      ) {
        filterTitle = (educationProvider as IEducationProvider).name
      } else if (filters.categories) {
        filterTitle =
          (categories as ICategory[]).find(
            (category) =>
              category._id ===
              (Array.isArray(filters.categories)
                ? filters.categories[0]
                : filters.categories)
          )?.name ?? ''
      }
    }

    return {
      props: {
        initialCoursesResponse: {
          pages: [initialCoursesResponse],
          pageParams: [page],
        },
        filterTitle,
        initialQueryParams: {
          page,
          limit,
          ...filters,
        },
        messages,
      },
    }
  } catch (e) {
    console.error(e)
    if (isBoom(e, 401)) {
      return {
        redirect: {
          destination: ROUTES.logout,
          permanent: false,
          locale,
        },
      }
    } else if (isBoom(e, 403)) {
      return {
        redirect: {
          destination: `/${locale}${ROUTES.accessDenied}`,
          permanent: false,
          locale,
        },
      }
    }

    throw e
  }
}

type SearchPageProps = {
  scopedApiKeys?: ScopedApiKeys
  [key: string]: unknown
}

export default function SearchPage(props: SearchPageProps) {
  if (EXTERNAL_SEARCH_CONFIG.enabled) {
    if (!props.scopedApiKeys) {
      throw new Error('scopedApiKeys is required when search is enabled')
    }
    return <ExternalSearchCoursesPage scopedApiKeys={props.scopedApiKeys} />
  }

  return <SearchCoursesPage {...props} />
}
